import React, { lazy, useEffect, Suspense, useState } from 'react';

import { AppRoutes } from 'routes/routes.enums';
import { useQueryParams } from 'hooks/useQueryParam';
import BlurPopup from 'components/BlurPopup';
import Loader from 'components/Loader';
import { DiveDocumentTypes, validationIdApi } from 'api/ValidationIdApi';
import * as notificationManager from 'notifications';
import { CheckedIcon } from 'assets/images';

import styles from './MobileSession.module.scss';

const ValidationIdPopup = lazy(
  () => import('pages/Dashboard/Identity/ValidationId/ValidationIdPopup/ValidationIdPopup'),
);

enum Page {
  Barcode = 'Barcode',
  Success = 'Success',
}

const SuccessPage = () => {
  return (
    <div className={styles.successWrapper}>
      <CheckedIcon className={styles.checkedIcon} />
      <p className={styles.text}>Thank you for verifying your ID.</p>
      <p className={styles.text}>Please return to your original device and exit QR code pop-up to continue.</p>
    </div>
  );
};

const MobileSession = () => {
  const params = useQueryParams();
  const [sessionId, setSessionId] = useState<string>(params.get('id') || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);
  const [page, setPage] = useState<Page>(Page.Barcode);
  const [documentType, setDocumentType] = useState<DiveDocumentTypes>(DiveDocumentTypes.ID);

  const checkSession = async () => {
    setLoading(true);

    try {
      const result = await validationIdApi.getValidationSession({
        sessionId: sessionId,
      });

      setDocumentType(result.documentType);

      setLoading(false);
    } catch (error) {
      console.error(`checkSession error: ${sessionId}`);
      console.error(error);
      window.location.replace(AppRoutes.Login);
    }
  };

  useEffect(() => {
    if (open) {
      checkSession();
    }
  }, [sessionId, open]);

  const onSuccess = async () => {
    setPage(Page.Success);
  };

  const onFailure = () => {
    setOpen(false);
    notificationManager.notifyFailure(notificationManager.FailureNotificationType.IdDocumentValidationError);
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    window.location.replace(AppRoutes.Login);
  };

  const validateDocument = (validateDoc: { sessionId: string; documentType: DiveDocumentTypes }) => {
    return async ({
      frontImage,
      backImage,
      faceImage,
    }: {
      frontImage: string;
      backImage?: string;
      faceImage?: string;
    }) =>
      await validationIdApi.validateDocumentsBySession({
        frontImage: frontImage,
        backImage: backImage,
        faceImage: faceImage,
        sessionId: validateDoc.sessionId,
        documentType: validateDoc.documentType,
      });
  };

  return (
    <>
      {loading ? (
        <Loader customContainerClassname={styles.loaderContainer} />
      ) : (
        <BlurPopup
          id="popup"
          classes={{
            paper: styles.paper,
            container: styles.container,
          }}
          onClose={onClose}
          open={open}
          scroll="body"
          blurDeep={2}
          isCloseButtonDisplayed={page === Page.Success}
        >
          <Suspense fallback={<Loader customContainerClassname={styles.loaderContainer} />}>
            {page === Page.Barcode ? (
              <ValidationIdPopup
                onSuccess={onSuccess}
                onFailure={onFailure}
                documentType={documentType}
                validateDocument={validateDocument({ sessionId: sessionId, documentType: documentType })}
              />
            ) : (
              <SuccessPage />
            )}
          </Suspense>
        </BlurPopup>
      )}
    </>
  );
};

export default MobileSession;
